import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Search, Clock, User } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { TextArea } from "@/components/TextArea";
import { Loading } from "@/components/Loading";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import {
  updateServiceOrderSchema,
  type UpdateServiceOrderFormData,
} from "@/schemas/serviceOrderSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetServiceOrderById,
  useUpdateServiceOrder,
  useGetServiceOrderHistory,
} from "@/hooks/useServiceOrders";
import { useGetSectors } from "@/hooks/useSectors";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetTeams } from "@/hooks/useTeams";
import { useGetOcurrences } from "@/hooks/useOccurrences";
import { MultiSelectTable } from "@/components/MultiSelectTable";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";

const priorityOptions = [
  { value: "LOW", label: "Baixa" },
  { value: "MEDIUM", label: "Média" },
  { value: "HIGH", label: "Alta" },
  { value: "URGENT", label: "Crítica" },
];

const statusLabels: Record<string, { label: string; color: string }> = {
  PENDING: { label: "Pendente", color: "bg-gray-500" },
  IN_PROGRESS: { label: "Em Andamento", color: "bg-blue-600" },
  SUSPENDED: { label: "Suspensa", color: "bg-yellow-600" },
  COMPLETED: { label: "Concluída", color: "bg-green-500" },
  CANCELLED: { label: "Cancelada", color: "bg-red-500" },
};

// Regras de transição de status
const statusTransitions: Record<string, string[]> = {
  PENDING: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["SUSPENDED", "COMPLETED", "CANCELLED"],
  SUSPENDED: ["IN_PROGRESS", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: [],
};

export const EditServiceOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    data: serviceOrderData,
    isLoading,
    error,
  } = useGetServiceOrderById(id!);
  const updateServiceOrder = useUpdateServiceOrder();
  const { data: historyData } = useGetServiceOrderHistory(id!);
  const { data: sectorsData } = useGetSectors();
  const { data: usersData } = useGetUsers({ role: "AGENT" });
  const { data: teamsData } = useGetTeams();
  const { data: occurrencesData } = useGetOcurrences({ limit: 50 });

  const history = historyData?.data || [];
  const [searchSector, setSearchSector] = useState("");
  const [searchResponsible, setSearchResponsible] = useState("");
  const [searchTeam, setSearchTeam] = useState("");

  // Estado para armazenar ocorrências originais
  const [originalOccurrenceIds, setOriginalOccurrenceIds] = useState<string[]>(
    [],
  );

  // Estado para rastrear prioridade original
  const [originalPriority, setOriginalPriority] = useState<string>("");

  // Estado para status atual e original
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [originalStatus, setOriginalStatus] = useState<string>("");
  const [statusJustification, setStatusJustification] = useState<string>("");

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UpdateServiceOrderFormData>({
    resolver: zodResolver(updateServiceOrderSchema),
    defaultValues: {
      title: "",
      description: "",
      priority: "MEDIUM",
      priorityJustification: "",
      sectorId: "",
      assignedToId: "",
      assignedTeamId: "",
      estimatedStartDate: "",
      estimatedEndDate: "",
      occurrenceIds: [],
    },
  });

  const occurrenceIds = watch("occurrenceIds");
  const currentPriority = watch("priority");

  // Verificar se a prioridade mudou
  const priorityChanged =
    originalPriority && currentPriority !== originalPriority;

  // Verificar se o status mudou
  const statusChanged = originalStatus && currentStatus !== originalStatus;

  // Funções para filtrar dados com pesquisa
  const filteredSectors =
    sectorsData?.data?.filter(
      (sector: any) =>
        !searchSector ||
        sector.name?.toLowerCase().includes(searchSector.toLowerCase()),
    ) || [];

  const filteredUsers =
    usersData?.data?.filter((user: any) => {
      if (!searchResponsible) return true;
      const fullName = user.profile?.fullName || "";
      const email = user.email || "";
      return (
        fullName.toLowerCase().includes(searchResponsible.toLowerCase()) ||
        email.toLowerCase().includes(searchResponsible.toLowerCase())
      );
    }) || [];

  const filteredTeams =
    teamsData?.data?.filter(
      (team: any) =>
        !searchTeam ||
        team.name?.toLowerCase().includes(searchTeam.toLowerCase()),
    ) || [];

  // Funções para gerenciar ocorrências
  const handleAddOccurrence = (occurrenceId: string) => {
    const newOccurrenceIds = [...(occurrenceIds || []), occurrenceId];
    setValue("occurrenceIds", newOccurrenceIds);
  };

  const handleRemoveOccurrence = (occurrenceId: string) => {
    const newOccurrenceIds = (occurrenceIds || []).filter(
      (id) => id !== occurrenceId,
    );
    setValue("occurrenceIds", newOccurrenceIds);
  };

  // Funções para gerenciar status
  const getAvailableStatusTransitions = () => {
    // Retorna os status permitidos de acordo com as regras de transição
    // Usa o status original (salvo no banco) como base, não o status atual selecionado
    const allowedTransitions = statusTransitions[originalStatus] || [];
    return allowedTransitions;
  };

  // Inicializar status assim que os dados da OS chegarem
  useEffect(() => {
    const serviceOrder = serviceOrderData || serviceOrderData?.data;
    if (serviceOrder?.status) {
      console.log("Inicializando status:", serviceOrder.status);
      setCurrentStatus(serviceOrder.status);
    }
  }, [serviceOrderData]);

  // Carregar dados da ordem de serviço no form
  useEffect(() => {
    // A API retorna os dados diretamente, não em serviceOrderData.data
    const serviceOrder = serviceOrderData || serviceOrderData?.data;

    // Só processa quando TODOS os dados necessários estão disponíveis
    if (
      serviceOrder &&
      serviceOrder.id &&
      sectorsData?.data &&
      usersData?.data &&
      teamsData?.data
    ) {
      console.log(
        "Todos os dados carregados - definindo valores do formulário",
      );
      console.log("Status da OS:", serviceOrder.status);

      // Define cada valor individualmente usando setValue
      setValue("title", serviceOrder.title || "");
      setValue("description", serviceOrder.description || "");
      setValue("priority", serviceOrder.priority || "MEDIUM");
      setValue("priorityJustification", "");
      setValue("sectorId", serviceOrder.sectorId || "");
      setValue("assignedToId", serviceOrder.assignedToId || "");
      setValue("assignedTeamId", serviceOrder.assignedTeamId || "");

      // Armazenar prioridade original
      setOriginalPriority(serviceOrder.priority || "MEDIUM");

      // Define o status
      const status = serviceOrder.status || "PENDING";
      console.log("Definindo currentStatus como:", status);
      setCurrentStatus(status);
      setOriginalStatus(status);
      setValue(
        "estimatedStartDate",
        serviceOrder.estimatedStartDate
          ? new Date(serviceOrder.estimatedStartDate).toISOString().slice(0, 16)
          : "",
      );
      setValue(
        "estimatedEndDate",
        serviceOrder.estimatedEndDate
          ? new Date(serviceOrder.estimatedEndDate).toISOString().slice(0, 16)
          : "",
      );
      const occurrenceIds =
        serviceOrder.items?.map((item: any) => item.occurrenceId) ||
        serviceOrder.occurrences?.map((occ: any) => occ.id) ||
        serviceOrder.occurrenceIds ||
        [];

      setValue("occurrenceIds", occurrenceIds);

      // Armazenar as ocorrências originais
      setOriginalOccurrenceIds(occurrenceIds);

      console.log("Valores definidos:");
      console.log("sectorId:", serviceOrder.sectorId);
      console.log("assignedToId:", serviceOrder.assignedToId);
      console.log("assignedTeamId:", serviceOrder.assignedTeamId);
    }
  }, [serviceOrderData, sectorsData, usersData, teamsData, setValue]);

  // Limpar justificativa quando prioridade volta ao original
  useEffect(() => {
    if (currentPriority === originalPriority) {
      setValue("priorityJustification", "");
    }
  }, [currentPriority, originalPriority, setValue]);

  // Limpar justificativa quando status volta ao original
  useEffect(() => {
    if (currentStatus === originalStatus) {
      setStatusJustification("");
    }
  }, [currentStatus, originalStatus]);

  const onSubmitUpdate = async (data: UpdateServiceOrderFormData) => {
    // Validar justificativa de status se status foi alterado
    if (statusChanged && !statusJustification.trim()) {
      toast.error(
        "Por favor, informe uma justificativa para a alteração de status",
      );
      return;
    }

    try {
      const payload = {
        id: id!,
        status: currentStatus,
        statusJustification: statusChanged ? statusJustification : undefined,
        originalOccurrenceIds: originalOccurrenceIds,
        ...data,
      };

      console.log("Dados de atualização enviados:", payload);

      await updateServiceOrder.mutateAsync(payload, {
        onSuccess: () => {
          navigate("/service-orders");
          toast.success("Ordem de serviço atualizada com sucesso!");
        },
      });
    } catch (error: any) {
      console.error("Erro completo:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);

      const errorMessage =
        error.response?.data?.message || "Erro ao atualizar ordem de serviço";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Editar Ordem de Serviço"
          description="Atualize os dados da ordem de serviço"
        />
        <div className="flex-1 flex items-center justify-center">
          <Loading label="Carregando ordem de serviço..." />
        </div>
      </div>
    );
  }

  if (error || !serviceOrderData) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Editar Ordem de Serviço"
          description="Atualize os dados da ordem de serviço"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              {error
                ? "Erro ao carregar ordem de serviço"
                : "Ordem de serviço não encontrada"}
            </p>
            <Button onClick={() => navigate("/service-orders")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Ordens de Serviço
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Editar Ordem de Serviço"
        description="Atualize os dados da ordem de serviço"
      />

      <div className="flex-1 overflow-y-auto mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <form
                    onSubmit={handleSubmit(onSubmitUpdate)}
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informações Básicas
                      </h3>
                      <div className="grid grid-cols-1 gap-4">
                        <Input
                          title="Título"
                          isRequired
                          placeholder="Ex: Reparos em infraestrutura - Rua Principal"
                          error={errors.title?.message}
                          {...register("title")}
                        />

                        <TextArea
                          title="Descrição"
                          isRequired
                          placeholder="Descreva os detalhes da ordem de serviço"
                          {...register("description")}
                        />
                        {errors.description && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.description.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Classificação e Atribuição
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                          name="priority"
                          control={control}
                          render={({ field }) => (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Prioridade{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione a prioridade" />
                                </SelectTrigger>
                                <SelectContent>
                                  {priorityOptions.map(
                                    (option: {
                                      value: string;
                                      label: string;
                                    }) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                              {errors.priority && (
                                <p className="text-sm text-red-500">
                                  {errors.priority.message}
                                </p>
                              )}
                            </div>
                          )}
                        />

                        {/* Campo de Status */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Status <span className="text-red-500">*</span>
                          </label>
                          <Select
                            value={currentStatus}
                            onValueChange={(value) => {
                              console.log(
                                "Status alterado de",
                                currentStatus,
                                "para",
                                value,
                              );
                              // Só atualiza se for um valor válido e diferente do atual
                              if (value && value !== currentStatus) {
                                setCurrentStatus(value);
                              }
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Aguardando...">
                                {currentStatus ? (
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`w-2 h-2 rounded-full ${statusLabels[currentStatus]?.color || "bg-gray-500"}`}
                                    />
                                    {statusLabels[currentStatus]?.label ||
                                      currentStatus}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">
                                    Aguardando...
                                  </span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {/* Status original */}
                              {originalStatus && (
                                <SelectItem value={originalStatus}>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`w-2 h-2 rounded-full ${statusLabels[originalStatus]?.color}`}
                                    />
                                    {statusLabels[originalStatus]?.label}{" "}
                                    (atual)
                                  </div>
                                </SelectItem>
                              )}

                              {/* Status disponíveis para transição (excluindo o original para não duplicar) */}
                              {getAvailableStatusTransitions()
                                .filter((status) => status !== originalStatus)
                                .map((status) => (
                                  <SelectItem key={status} value={status}>
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`w-2 h-2 rounded-full ${statusLabels[status]?.color}`}
                                      />
                                      {statusLabels[status]?.label}
                                    </div>
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Justificativa da mudança de status */}
                      {statusChanged && (
                        <div className="space-y-2 mt-4">
                          <label className="text-sm font-medium">
                            Justificativa da mudança de status{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <TextArea
                            placeholder="Informe o motivo da alteração do status..."
                            rows={3}
                            value={statusJustification}
                            onChange={(e) =>
                              setStatusJustification(e.target.value)
                            }
                          />
                          <p className="text-xs text-gray-500">
                            Status alterado de{" "}
                            <span className="font-medium">
                              {statusLabels[originalStatus]?.label ||
                                originalStatus}
                            </span>{" "}
                            para{" "}
                            <span className="font-medium">
                              {statusLabels[currentStatus]?.label ||
                                currentStatus}
                            </span>
                          </p>
                        </div>
                      )}

                      {/* Justificativa da mudança de prioridade */}
                      {priorityChanged && (
                        <div className="space-y-2 mt-4">
                          <label className="text-sm font-medium">
                            Justificativa da mudança de prioridade{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <TextArea
                            placeholder="Informe o motivo da alteração da prioridade..."
                            rows={3}
                            errorMessage={errors.priorityJustification?.message}
                            {...register("priorityJustification", {
                              required: priorityChanged
                                ? "Justificativa é obrigatória quando a prioridade é alterada"
                                : false,
                            })}
                          />
                          <p className="text-xs text-gray-500">
                            Prioridade alterada de{" "}
                            <span className="font-medium">
                              {
                                priorityOptions.find(
                                  (p) => p.value === originalPriority,
                                )?.label
                              }
                            </span>{" "}
                            para{" "}
                            <span className="font-medium">
                              {
                                priorityOptions.find(
                                  (p) => p.value === currentPriority,
                                )?.label
                              }
                            </span>
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Controller
                          name="sectorId"
                          control={control}
                          render={({ field }) => (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Setor <span className="text-red-500">*</span>
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                                key={`sector-${field.value}`} // Força re-render quando valor muda
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o setor" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="flex items-center px-3 pb-2">
                                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                    <Input
                                      placeholder="Pesquisar setor..."
                                      value={searchSector}
                                      onChange={(e) =>
                                        setSearchSector(e.target.value)
                                      }
                                      className="h-8 w-full border-none p-0 focus-visible:ring-0"
                                    />
                                  </div>
                                  {sectorsData?.data &&
                                  sectorsData.data.length > 0 ? (
                                    filteredSectors.map((sector: any) => (
                                      <SelectItem
                                        key={sector.id}
                                        value={sector.id}
                                      >
                                        {sector.name}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                      {sectorsData
                                        ? "Nenhum setor encontrado."
                                        : "Carregando setores..."}
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                              {errors.sectorId && (
                                <p className="text-sm text-red-500">
                                  {errors.sectorId.message}
                                </p>
                              )}
                            </div>
                          )}
                        />

                        <Controller
                          name="assignedToId"
                          control={control}
                          render={({ field }) => (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Responsável{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                                key={`user-${field.value}`} // Força re-render quando valor muda
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o responsável" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="flex items-center px-3 pb-2">
                                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                    <Input
                                      placeholder="Pesquisar responsável..."
                                      value={searchResponsible}
                                      onChange={(e) =>
                                        setSearchResponsible(e.target.value)
                                      }
                                      className="h-8 w-full border-none p-0 focus-visible:ring-0"
                                    />
                                  </div>
                                  {usersData?.data &&
                                  usersData.data.length > 0 ? (
                                    filteredUsers.map((user: any) => (
                                      <SelectItem key={user.id} value={user.id}>
                                        {user.profile?.fullName || user.email}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                      {usersData
                                        ? "Nenhum usuário encontrado."
                                        : "Carregando usuários..."}
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                              {errors.assignedToId && (
                                <p className="text-sm text-red-500">
                                  {errors.assignedToId.message}
                                </p>
                              )}
                            </div>
                          )}
                        />

                        <Controller
                          name="assignedTeamId"
                          control={control}
                          render={({ field }) => (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Equipe <span className="text-red-500">*</span>
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                                key={`team-${field.value}`} // Força re-render quando valor muda
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione a equipe" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="flex items-center px-3 pb-2">
                                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                    <Input
                                      placeholder="Pesquisar equipe..."
                                      value={searchTeam}
                                      onChange={(e) =>
                                        setSearchTeam(e.target.value)
                                      }
                                      className="h-8 w-full border-none p-0 focus-visible:ring-0"
                                    />
                                  </div>
                                  {teamsData?.data &&
                                  teamsData.data.length > 0 ? (
                                    filteredTeams.map((team: any) => (
                                      <SelectItem key={team.id} value={team.id}>
                                        {team.name}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                      {teamsData
                                        ? "Nenhuma equipe encontrada."
                                        : "Carregando equipes..."}
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                              {errors.assignedTeamId && (
                                <p className="text-sm text-red-500">
                                  {errors.assignedTeamId.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Ocorrências Relacionadas
                      </h3>
                      <MultiSelectTable
                        title="Ocorrências"
                        items={occurrencesData?.data || []}
                        selectedIds={occurrenceIds || []}
                        onAdd={handleAddOccurrence}
                        onRemove={handleRemoveOccurrence}
                        searchPlaceholder="Pesquisar por protocolo..."
                        emptyMessage="Nenhuma ocorrência adicionada"
                        displayField={(occurrence: any) =>
                          `${occurrence.protocol || "Sem protocolo"} - ${occurrence.description?.substring(0, 50) || "Sem descrição"}...`
                        }
                      />
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Prazos Estimados
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Data de Início Estimada"
                          isRequired
                          type="datetime-local"
                          error={errors.estimatedStartDate?.message}
                          {...register("estimatedStartDate")}
                        />

                        <Input
                          title="Data de Término Estimada"
                          isRequired
                          type="datetime-local"
                          error={errors.estimatedEndDate?.message}
                          {...register("estimatedEndDate")}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/service-orders")}
                        disabled={updateServiceOrder.isPending}
                        className="cursor-pointer"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                      </Button>
                      <Button
                        type="submit"
                        disabled={updateServiceOrder.isPending}
                        isLoading={updateServiceOrder.isPending}
                        className="text-white cursor-pointer"
                      >
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Atualizar
                        </>
                      </Button>
                    </div>
                  </form>
                </CardContentUI>
              </Card>
            </motion.div>
          </div>

          {/* Sidebar - Histórico */}
          <div className="lg:col-span-1">
            <Card className="bg-white h-full">
              <CardContentUI className="p-6 h-full flex flex-col">
                <h3 className="text-lg font-semibold mb-4">Histórico</h3>
                {history.length > 0 ? (
                  <div className="space-y-3 overflow-y-auto flex-1">
                    {history.map((item: any) => {
                      const actionLabels: Record<string, string> = {
                        created: "Criada",
                        status_changed: "Status alterado",
                        priority_changed: "Prioridade alterada",
                        assigned_changed: "Responsável alterado",
                      };

                      return (
                        <div
                          key={item.id}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900">
                                  {actionLabels[item.action] || item.action}
                                </p>
                                {item.action === "status_changed" && (
                                  <div className="flex items-center gap-1.5 text-xs mt-1 flex-wrap">
                                    <Badge
                                      className={`text-xs py-0 ${statusLabels[item.fromValue]?.color || "bg-gray-500"}`}
                                    >
                                      {statusLabels[item.fromValue]?.label ||
                                        item.fromValue}
                                    </Badge>
                                    <span className="text-gray-400">→</span>
                                    <Badge
                                      className={`text-xs py-0 ${statusLabels[item.toValue]?.color || "bg-gray-500"}`}
                                    >
                                      {statusLabels[item.toValue]?.label ||
                                        item.toValue}
                                    </Badge>
                                  </div>
                                )}
                                {item.justification && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    <span className="font-medium">
                                      Justificativa:
                                    </span>{" "}
                                    {item.justification}
                                  </p>
                                )}
                                <div className="flex flex-col gap-1 text-xs text-gray-500 mt-2">
                                  <span>
                                    {new Date(item.performedAt).toLocaleString(
                                      "pt-BR",
                                    )}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {item.performedBy}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Nenhum histórico disponível.
                  </p>
                )}
              </CardContentUI>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
